import { createSlice } from '@reduxjs/toolkit';

import { hydrateSnapshot } from '@/app/store/stateSnapshot/hydrateSnapshotAction';
/* THIS STORE USED FOR CACHING VARIOUS DATA LISTS TO AVOID REDUNDANT API CALLS THROUGHOUT THE APP */

export interface CacheItem {
    cd: string;
    lbl?: string | null;
    dsc?: string | null;
    prntCd?: string | null;
}

export interface PackageConfiguration {
    cd: string;
    dsc: string;
    extDimUom: 'CM' | 'IN' | string;
    extH: number;
    extL: number;
    extMeasTyCd: string;
    extMeasTyDsc: string;
    extW: number;
    extWgt: number;
    extWgtUom: 'KG' | 'LB' | string;
    lbl: string;
    prntCd: string;
}

export interface Country extends CacheItem {
    isoCtryCode: string;
    ctryCd: string;
    isoCtryNm: string;
    isoCur: string;
    code: string;
    isoCurNm: string;
    wgtUom: string;
    dimUom: string;
    poLocTy: string;
    poLocTyDsc: string;
    tmFmtStrct: string;
    dtFmtStrct: string;
    intlDlInTelNo: string;
    rgnCd: string;
    indemnityLtrRqrInd: string;
    sanctionedCountry: string;
    unSanctionedCountry: string;
    onboarded: string;
    phnPttrn: string | null;
    poCdPttrn: string;
    sgstdPoCd: string;
    dflLngCd: string;
    ctryMngrEml: string[];
    sprtdLng: string[];
    puLeadTmMin: number;
    ctryMrkUrl: string;
    ctryShpTrcUrl: string;
    frdAwrUrl: string;
    lglNotcUrl: string;
    prvNotcUrl: string;
    addInfoUrl: string;
    trmOfUseUrl: string;
    signRqr: 'Y' | 'N';
    shpHldPrd: number;
    shpExpTrshld: number;
    payAccptdTlrcAmt: number;
    maxExtDay: number;
}

export interface ServicePoint {
    srvPntFcId: string;
    srvPntNm: string;
    ctryCd?: string;
    srvPntCtryCd?: string;
    srvPntAddrLn1?: string;
    srvPntCtyNm?: string;
    srvPntZip?: string;
}

/* CACHE STATE INTERFACE */
export interface CacheState {
    typeOfExport: CacheItem[];
    countries: Country[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    languages: any[];
    currencies: CacheItem[];
    shipmentTypes: CacheItem[];
    weightUoms: CacheItem[];
    dimensionUoms: CacheItem[];
    dropoutReasons: CacheItem[];
    packageTypes: Partial<PackageConfiguration>[];
    roleTypes: CacheItem[];
    customerSector: CacheItem[];
    lineItemUom: CacheItem[];
    global: CacheItem[];
    servicePoint: ServicePoint[];
    adminStatus: CacheItem[];
    quotationStatus: CacheItem[];
    invoiceType: CacheItem[];
    shipmentPurpose: CacheItem[];
    customerType: CacheItem[];
    region: CacheItem[];
    servicePointAccType: CacheItem[];
    facilityType: CacheItem[];
    retailStoreType: CacheItem[];
    labelTemplate: CacheItem[];
    archiveLabelTemplate: CacheItem[];
    commercialInvoiceTemplate: CacheItem[];
    paymentMethod: CacheItem[];
    cmpConsentMethod: CacheItem[];
    servicePointType: CacheItem[];
    servicePointProfileType: CacheItem[];
    incoterm: CacheItem[];
    shipmentStatus: CacheItem[];
    shipmentInventoryPhase: CacheItem[];
    shipmentInventoryStatus: CacheItem[];
    shipmentCancellationReasons: CacheItem[];
    shipmentDocumentTypes: CacheItem[];
    promocodeInd: CacheItem[];
    promoCodeStatus: CacheItem[];
    hierarchy: CacheItem[];
    notificationType: CacheItem[];
    frontend: CacheItem[];
    nonAccSearchTypes: CacheItem[];
    phoneTypes: CacheItem[];
    sorting: CacheItem[];
    loginCountryLanguage: CacheItem[];
    accountBillingTypes: CacheItem[];
    inventoryPurposeCodes: CacheItem[];
    damageParts: CacheItem[];
    receiverTypes: CacheItem[];
    collectionRefusalReasons: CacheItem[];
    shareMethods: CacheItem[];
    globalProducts: CacheItem[];
    inventoryTabFilterCodes: CacheItem[];
    holdExtensionReasons: CacheItem[];
}

export const initialCacheState: CacheState = {
    typeOfExport: [],
    countries: [],
    languages: [],
    currencies: [],
    shipmentTypes: [],
    weightUoms: [],
    dimensionUoms: [],
    dropoutReasons: [],
    packageTypes: [],
    roleTypes: [],
    customerSector: [],
    lineItemUom: [],
    global: [],
    servicePoint: [],
    adminStatus: [],
    quotationStatus: [],
    invoiceType: [],
    shipmentPurpose: [],
    customerType: [],
    region: [],
    servicePointAccType: [],
    facilityType: [],
    retailStoreType: [],
    labelTemplate: [],
    archiveLabelTemplate: [],
    commercialInvoiceTemplate: [],
    paymentMethod: [],
    cmpConsentMethod: [],
    servicePointType: [],
    servicePointProfileType: [],
    incoterm: [],
    shipmentStatus: [],
    shipmentInventoryPhase: [],
    shipmentInventoryStatus: [],
    shipmentCancellationReasons: [],
    shipmentDocumentTypes: [],
    promocodeInd: [],
    promoCodeStatus: [],
    hierarchy: [],
    notificationType: [],
    frontend: [],
    nonAccSearchTypes: [],
    phoneTypes: [],
    sorting: [],
    loginCountryLanguage: [],
    accountBillingTypes: [],
    inventoryPurposeCodes: [],
    damageParts: [],
    receiverTypes: [],
    collectionRefusalReasons: [],
    shareMethods: [],
    globalProducts: [],
    inventoryTabFilterCodes: [],
    holdExtensionReasons: [],
};

const cacheSlice = createSlice({
    name: 'cache',
    initialState: initialCacheState,
    reducers: {
        setCache(state, action) {
            Object.assign(state, action.payload.dataList);
        },

        clearCache(state) {
            state.countries = [];
            state.languages = [];
            state.currencies = [];
            state.shipmentTypes = [];
            state.weightUoms = [];
            state.dimensionUoms = [];
            state.dropoutReasons = [];
            state.packageTypes = [];
            state.roleTypes = [];
            state.customerSector = [];
            state.lineItemUom = [];
            state.global = [];
            state.servicePoint = [];
            state.adminStatus = [];
            state.quotationStatus = [];
            state.invoiceType = [];
            state.shipmentPurpose = [];
            state.customerType = [];
            state.region = [];
            state.servicePointAccType = [];
            state.facilityType = [];
            state.retailStoreType = [];
            state.labelTemplate = [];
            state.archiveLabelTemplate = [];
            state.commercialInvoiceTemplate = [];
            state.paymentMethod = [];
            state.cmpConsentMethod = [];
            state.servicePointType = [];
            state.servicePointProfileType = [];
            state.incoterm = [];
            state.shipmentStatus = [];
            state.shipmentInventoryPhase = [];
            state.shipmentInventoryStatus = [];
            state.shipmentCancellationReasons = [];
            state.shipmentDocumentTypes = [];
            state.promocodeInd = [];
            state.promoCodeStatus = [];
            state.hierarchy = [];
            state.notificationType = [];
            state.nonAccSearchTypes = [];
            state.phoneTypes = [];
            state.loginCountryLanguage = [];
            state.accountBillingTypes = [];
            state.inventoryPurposeCodes = [];
            state.damageParts = [];
            state.receiverTypes = [];
            state.collectionRefusalReasons = [];
            state.globalProducts = [];
            state.inventoryTabFilterCodes = [];
            state.holdExtensionReasons = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(hydrateSnapshot, (state, action) => {
            const persisted = action.payload.cache;
            if (!persisted) return;
            // Only restore the 4 shell keys that are persisted in the snapshot.
            if (persisted.countries?.length) state.countries = persisted.countries;
            if (persisted.languages?.length) state.languages = persisted.languages;
            if (persisted.servicePoint?.length) state.servicePoint = persisted.servicePoint;
            if (persisted.loginCountryLanguage?.length)
                state.loginCountryLanguage = persisted.loginCountryLanguage;
        });
    },
});

export const { setCache, clearCache } = cacheSlice.actions;
export default cacheSlice.reducer;
